import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { CircleCheck, CircleDashed } from "lucide-react";

import useApiMethods from "../../lib/useApiMethods";
import { APPRAISAL_AUDIT } from "../../constants/api";
import StatusPill from "./StatusPill";

const AuditTimeline = ({ appraisalId, refreshKey = 0 }) => {
    const { fetch } = useApiMethods();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!appraisalId) return;
        let cancelled = false;
        setLoading(true);
        fetch(APPRAISAL_AUDIT, { id: appraisalId })
            .then((res) => { if (!cancelled) setEntries(res?.data || []); })
            .catch(() => { if (!cancelled) setEntries([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // refreshKey lets the parent force a reload after a status transition.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appraisalId, refreshKey]);

    if (loading) {
        return <div className="text-center py-4 text-sm text-muted-foreground">Loading history…</div>;
    }

    if (!entries.length) {
        return (
            <div className="text-center py-6 text-sm text-muted-foreground">
                No activity yet.
            </div>
        );
    }

    return (
        <ol className="relative border-l border-gray-200 dark:border-gray-700 ml-3">
            {entries.map((e, idx) => {
                const actor = e.performed_by;
                const isLast = idx === entries.length - 1;
                return (
                    <li key={e.id} className="ml-6 pb-5 last:pb-0">
                        <span className="absolute -left-2.5 flex items-center justify-center w-5 h-5 rounded-full bg-white dark:bg-gray-900">
                            {isLast ? (
                                <CircleDashed className="w-4 h-4 text-sidebar-primary" />
                            ) : (
                                <CircleCheck className="w-4 h-4 text-emerald-500" />
                            )}
                        </span>
                        <div className="flex flex-wrap items-center gap-2">
                            <StatusPill value={e.to_status} />
                            <span className="text-xs text-muted-foreground">
                                {dayjs(e.created_date).format("DD MMM YYYY, hh:mm A")}
                            </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-800 dark:text-gray-200">
                            <span className="font-medium">{actor?.name || "System"}</span>
                            {actor?.designation && <span className="text-muted-foreground"> · {actor.designation}</span>}
                        </p>
                        {e.comment && (
                            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 italic">
                                “{e.comment}”
                            </p>
                        )}
                    </li>
                );
            })}
        </ol>
    );
};

export default AuditTimeline;
